import React, { useState, useCallback } from "react";
import {
  TokenGroup,
  TokenGroupProps,
  NonCancelableCustomEvent,
} from "@amzn/awsui-components-react-v3";
import { StringObjectType } from "../../wizard/StringObjectType";
import wrapperStyles from "./FormFieldMultiSelectWrapper.module.css";
import { AutoSuggestItem } from "../../search/AutoSuggestResponse";

interface FormFieldMultiSelectWrapperProps<
  TSelectionData extends StringObjectType<TSelectionData>
> {
  values: AutoSuggestItem[];
  onUpdateValues: (ids: AutoSuggestItem[]) => void;
  getIdFromData: (data: TSelectionData) => string;
  buildTokenFromData: (data: TSelectionData) => TokenGroupProps.Item;
  render: (onSelect: (data: TSelectionData) => void) => JSX.Element;
  roundTokenImages?: boolean;
}

const DEFAULT_EMPTY_VALUES: AutoSuggestItem[] = [];

function FormFieldMultiSelectWrapper<
  TSelectionData extends StringObjectType<TSelectionData>
>({
  values = DEFAULT_EMPTY_VALUES,
  onUpdateValues,
  getIdFromData,
  buildTokenFromData,
  render,
  roundTokenImages,
}: FormFieldMultiSelectWrapperProps<TSelectionData>): JSX.Element {
  const [selectionDataCache, setSelectionDataCache] = useState<
    Record<string, TSelectionData>
  >({});

  const tokenItems = values.map((item) => {
    if (!selectionDataCache[item.urlId]) {
      throw new Error(
        "FormFieldMultiSelectWrapper initialization with values not implemented!"
      );
    }

    return buildTokenFromData(selectionDataCache[item.urlId]);
  });

  const onSelect = useCallback(
    (data: TSelectionData): void => {
      const id = getIdFromData(data);
      if (values.some((value) => value.urlId === id)) {
        // Don't add duplicates.
        return;
      }

      setSelectionDataCache((current) => ({
        ...current,
        [getIdFromData(data)]: data,
      }));

      onUpdateValues(values.concat(data as AutoSuggestItem));
    },
    [getIdFromData, values, onUpdateValues]
  );

  const onDismiss = (
    event: NonCancelableCustomEvent<{ itemIndex: number }>
  ): void => {
    onUpdateValues(
      values.filter((id) => id !== values[event.detail.itemIndex])
    );
  };

  return (
    <React.Fragment>
      {render(onSelect)}
      <div className={roundTokenImages ? wrapperStyles["token-holder"] : ""}>
        <TokenGroup items={tokenItems} onDismiss={onDismiss}></TokenGroup>
      </div>
    </React.Fragment>
  );
}

export default FormFieldMultiSelectWrapper;
